import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import {
  Upload, Plus, Trash2, Eye, EyeOff, Type, Loader2, GripVertical,
  ChevronUp, ChevronDown, Image as ImageIcon, ZoomIn, Move
} from "lucide-react";

const FONTS = [
  { id: "Arial, sans-serif", label: "Arial" },
  { id: "'Georgia', serif", label: "Georgia" },
  { id: "'Times New Roman', serif", label: "Times New Roman" },
  { id: "'Verdana', sans-serif", label: "Verdana" },
  { id: "'Trebuchet MS', sans-serif", label: "Trebuchet MS" },
  { id: "'Courier New', monospace", label: "Courier New" },
  { id: "'Tahoma', sans-serif", label: "Tahoma" },
];

const VARIABLES = [
  { id: "{{nombre_usuario}}", label: "Nombre" },
  { id: "{{departamento}}", label: "Departamento" },
  { id: "{{fecha_reintegro}}", label: "Fecha Reintegro" },
  { id: "{{fecha_inicio_vacaciones}}", label: "Fecha Inicio" },
  { id: "{{fecha_fin_vacaciones}}", label: "Fecha Fin" },
  { id: "{{dias_vacaciones}}", label: "Dias" },
];

const DEFAULT_LAYER = {
  text: "",
  x: 50,
  y: 50,
  fontSize: 24,
  color: "#1a1a1a",
  fontFamily: "Arial, sans-serif",
  fontWeight: "normal",
  fontStyle: "normal",
  textAlign: "center",
  textShadow: "none",
  visible: true,
};

// Normalize stored bgImage URL to current API domain
function normalizeBgUrl(url) {
  if (!url) return "";
  if (url.includes("/api/uploads/")) {
    const baseUrl = API.replace(/\/api$/, "");
    return `${baseUrl}/api/uploads/${url.split("/api/uploads/")[1]}`;
  }
  return url;
}

export default function VisualEmailEditor({ formData, setFormData, getAuthHeaders }) {
  const [bgImage, setBgImage] = useState(normalizeBgUrl(formData._visualConfig?.bgImage || ""));
  const [bgScale, setBgScale] = useState(formData._visualConfig?.bgScale || 1);
  const [layers, setLayers] = useState(formData._visualConfig?.layers || []);
  const [preImageText, setPreImageText] = useState(formData._visualConfig?.preImageText || "");
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [renderingPreview, setRenderingPreview] = useState(false);
  const [dragging, setDragging] = useState(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Sync visual config to formData
  const syncToFormData = useCallback((newBg, newLayers, newScale, newPreText) => {
    const sc = newScale ?? bgScale;
    const pt = newPreText ?? preImageText;
    setFormData(prev => ({
      ...prev,
      contenido: `<!-- visual_template -->`,
      _visualConfig: { bgImage: newBg, layers: newLayers, bgScale: sc, preImageText: pt }
    }));
  }, [bgScale, preImageText, setFormData]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imagenes");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload/imagen`, fd, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      const url = res.data.url || "";
      const baseUrl = API.replace(/\/api$/, "");
      const fullUrl = `${baseUrl}/api${url}`;
      setBgImage(fullUrl);
      syncToFormData(fullUrl, layers, bgScale);
      toast.success("Imagen cargada");
    } catch {
      toast.error("Error al subir imagen");
    } finally {
      setUploading(false);
    }
  };

  const addLayer = () => {
    const newLayer = { ...DEFAULT_LAYER, text: "Texto nuevo", id: Date.now() };
    const newLayers = [...layers, newLayer];
    setLayers(newLayers);
    setSelectedLayer(newLayers.length - 1);
    syncToFormData(bgImage, newLayers);
  };

  const updateLayer = (idx, updates) => {
    const newLayers = layers.map((l, i) => i === idx ? { ...l, ...updates } : l);
    setLayers(newLayers);
    syncToFormData(bgImage, newLayers);
  };

  const removeLayer = (idx) => {
    const newLayers = layers.filter((_, i) => i !== idx);
    setLayers(newLayers);
    setSelectedLayer(null);
    syncToFormData(bgImage, newLayers);
  };

  const moveLayer = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= layers.length) return;
    const newLayers = [...layers];
    [newLayers[idx], newLayers[newIdx]] = [newLayers[newIdx], newLayers[idx]];
    setLayers(newLayers);
    setSelectedLayer(newIdx);
    syncToFormData(bgImage, newLayers);
  };

  const insertVariable = (varId) => {
    if (selectedLayer === null) {
      toast.error("Seleccione una capa de texto primero");
      return;
    }
    const layer = layers[selectedLayer];
    updateLayer(selectedLayer, { text: layer.text + varId });
  };

  const handleBgScaleChange = (val) => {
    const newScale = val[0];
    setBgScale(newScale);
    syncToFormData(bgImage, layers, newScale);
  };

  // === Drag & Drop via mouse events ===
  const handleMouseDown = (e, idx) => {
    if (previewMode) return;
    e.preventDefault();
    e.stopPropagation();
    setSelectedLayer(idx);
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDragging({
      idx,
      startX: e.clientX,
      startY: e.clientY,
      startLayerX: layers[idx].x,
      startLayerY: layers[idx].y,
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const imgEl = canvas.querySelector("img");
    if (!imgEl) return;
    const rect = imgEl.getBoundingClientRect();
    const dx = e.clientX - dragging.startX;
    const dy = e.clientY - dragging.startY;
    const dxPct = (dx / rect.width) * 100;
    const dyPct = (dy / rect.height) * 100;
    const newX = Math.max(0, Math.min(100, dragging.startLayerX + dxPct));
    const newY = Math.max(0, Math.min(100, dragging.startLayerY + dyPct));
    // Update layer position directly (no sync to avoid perf issues)
    setLayers(prev => prev.map((l, i) => i === dragging.idx ? { ...l, x: Math.round(newX * 10) / 10, y: Math.round(newY * 10) / 10 } : l));
  }, [dragging]);

  const handleMouseUp = useCallback(() => {
    if (dragging) {
      syncToFormData(bgImage, layers);
      setDragging(null);
    }
  }, [dragging, bgImage, layers, syncToFormData]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  // === Preview: call backend render ===
  const generatePreview = async () => {
    if (!bgImage) return;
    setRenderingPreview(true);
    setPreviewUrl("");
    try {
      // Replace variables with example data for preview layers
      const previewLayers = layers.map(l => ({
        ...l,
        text: l.text
          .replace(/\{\{nombre_usuario\}\}/g, "Grace Baez")
          .replace(/\{\{departamento\}\}/g, "Riesgos de Personas")
          .replace(/\{\{fecha_reintegro\}\}/g, "23 de marzo del 2026")
          .replace(/\{\{fecha_inicio_vacaciones\}\}/g, "10/03/2026")
          .replace(/\{\{fecha_fin_vacaciones\}\}/g, "20/03/2026")
          .replace(/\{\{dias_vacaciones\}\}/g, "10")
      }));
      // Extract relative URL from bgImage for backend
      let bgForBackend = bgImage;
      if (bgImage.includes("/api/uploads/")) {
        bgForBackend = "/uploads/" + bgImage.split("/api/uploads/")[1];
      } else if (bgImage.includes("/uploads/")) {
        bgForBackend = "/uploads/" + bgImage.split("/uploads/")[1];
      }
      const res = await axios.post(`${API}/plantillas-correo/render-visual`, {
        bgImage: bgForBackend,
        layers: previewLayers,
        canvasWidth: 600,
        bgScale: bgScale,
      }, getAuthHeaders());
      const baseUrl = API.replace(/\/api$/, "");
      setPreviewUrl(`${baseUrl}/api${res.data.url}`);
    } catch (err) {
      toast.error("Error al generar vista previa");
      console.error(err);
    } finally {
      setRenderingPreview(false);
    }
  };

  const togglePreview = async () => {
    if (!previewMode) {
      await generatePreview();
    }
    setPreviewMode(!previewMode);
  };

  const handlePreImageTextChange = (e) => {
    const val = e.target.value;
    setPreImageText(val);
    syncToFormData(bgImage, layers, bgScale, val);
  };

  // Replace variables with example data for local preview text
  const previewText = (text) => {
    return text
      .replace(/\{\{nombre_usuario\}\}/g, "Grace Baez")
      .replace(/\{\{departamento\}\}/g, "Riesgos de Personas")
      .replace(/\{\{fecha_reintegro\}\}/g, "23 de marzo del 2026")
      .replace(/\{\{fecha_inicio_vacaciones\}\}/g, "10/03/2026")
      .replace(/\{\{fecha_fin_vacaciones\}\}/g, "20/03/2026")
      .replace(/\{\{dias_vacaciones\}\}/g, "10");
  };

  return (
    <div className="flex flex-col h-full min-h-[500px] overflow-y-auto" data-testid="visual-email-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-2 pb-3 border-b mb-3 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-bg-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
          {bgImage ? "Cambiar Imagen" : "Subir Imagen"}
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

        <Button size="sm" variant="outline" onClick={addLayer} disabled={!bgImage} data-testid="add-layer-btn">
          <Plus className="w-4 h-4 mr-1" /> Agregar Texto
        </Button>

        <div className="h-6 w-px bg-slate-200 mx-1" />

        <div className="flex gap-1 flex-wrap">
          {VARIABLES.map(v => (
            <Button key={v.id} size="sm" variant="ghost" className="text-xs h-7 px-2 bg-blue-50 text-blue-700 hover:bg-blue-100" onClick={() => insertVariable(v.id)} data-testid={`var-btn-${v.label}`}>
              {v.label}
            </Button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {bgImage && (
            <div className="flex items-center gap-2 mr-2" data-testid="zoom-control">
              <ZoomIn className="w-4 h-4 text-slate-500" />
              <Slider
                value={[bgScale]}
                onValueChange={handleBgScaleChange}
                min={0.3}
                max={2}
                step={0.1}
                className="w-24"
              />
              <span className="text-xs text-slate-500 w-10">{Math.round(bgScale * 100)}%</span>
            </div>
          )}
          <Button size="sm" variant={previewMode ? "default" : "outline"} onClick={togglePreview} disabled={!bgImage || renderingPreview} data-testid="preview-toggle-btn">
            {renderingPreview ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : previewMode ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {previewMode ? "Editar" : "Vista Previa"}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {/* Pre-image text block */}
        {bgImage && (
          <div className="bg-white rounded-lg border p-3 flex-shrink-0" data-testid="pre-image-text-section">
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Texto antes de la imagen (aparece en el correo antes de la imagen)
            </Label>
            <textarea
              value={preImageText}
              onChange={handlePreImageTextChange}
              placeholder="Escriba aquí el texto que aparecerá antes de la imagen en el correo. Los saltos de línea se respetan."
              className="w-full min-h-[80px] p-2 text-sm border rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="pre-image-text-input"
            />
            <p className="text-xs text-slate-400 mt-1">
              Puede usar variables: {"{{nombre_usuario}}"}, {"{{departamento}}"}, {"{{dias_vacaciones}}"}, etc. Los saltos de línea se respetarán en el correo.
            </p>
          </div>
        )}

        <div className="flex gap-3" style={{ minHeight: 350 }}>
        {/* Canvas / Preview */}
        <div className="flex-1 bg-slate-100 rounded-lg border overflow-auto flex items-start justify-center p-4">
          {!bgImage ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3">
              <ImageIcon className="w-16 h-16" />
              <p className="text-sm">Suba una imagen de fondo para comenzar</p>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-bg-empty-btn">
                <Upload className="w-4 h-4 mr-2" /> Subir Imagen
              </Button>
            </div>
          ) : previewMode && previewUrl ? (
            <div style={{ width: "100%", maxWidth: 560 }} data-testid="preview-rendered">
              <p className="text-xs text-slate-500 mb-2 text-center">Vista previa renderizada por el servidor (identica al correo final)</p>
              <img src={previewUrl} alt="Vista previa" style={{ width: "100%", display: "block", borderRadius: 8 }} />
            </div>
          ) : (
            <div
              ref={canvasRef}
              style={{ position: "relative", width: "100%", maxWidth: 560, cursor: dragging ? "grabbing" : "default" }}
              data-testid="visual-canvas"
            >
              <img src={bgImage} alt="Fondo" style={{ width: "100%", display: "block", borderRadius: 8 }} />
              {layers.map((layer, idx) => layer.visible && (
                <div
                  key={layer.id || idx}
                  data-testid={`text-layer-${idx}`}
                  onMouseDown={(e) => handleMouseDown(e, idx)}
                  onClick={() => !previewMode && setSelectedLayer(idx)}
                  style={{
                    position: "absolute",
                    left: `${layer.x}%`,
                    top: `${layer.y}%`,
                    transform: "translate(-50%, -50%)",
                    fontSize: `${layer.fontSize}px`,
                    color: layer.color,
                    fontFamily: layer.fontFamily,
                    fontWeight: layer.fontWeight,
                    fontStyle: layer.fontStyle,
                    textAlign: layer.textAlign,
                    textShadow: layer.textShadow !== "none" ? layer.textShadow : undefined,
                    whiteSpace: "nowrap",
                    cursor: dragging?.idx === idx ? "grabbing" : "grab",
                    outline: selectedLayer === idx ? "2px dashed #3b82f6" : "none",
                    outlineOffset: 4,
                    padding: "2px 6px",
                    borderRadius: 4,
                    userSelect: "none",
                  }}
                >
                  {previewMode ? previewText(layer.text) : layer.text}
                  {selectedLayer === idx && !dragging && (
                    <Move className="w-3 h-3 absolute -top-4 -right-4 text-blue-500" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Layer Properties Panel */}
        {!previewMode && bgImage && (
          <div className="w-72 flex-shrink-0 bg-white rounded-lg border overflow-y-auto" data-testid="layers-panel">
            <div className="p-3 border-b bg-slate-50">
              <p className="font-semibold text-sm text-slate-700">Capas de Texto</p>
            </div>

            {/* Layer list */}
            <div className="divide-y">
              {layers.map((layer, idx) => (
                <div
                  key={layer.id || idx}
                  className={`p-2 cursor-pointer transition-colors ${selectedLayer === idx ? "bg-blue-50" : "hover:bg-slate-50"}`}
                  onClick={() => setSelectedLayer(idx)}
                  data-testid={`layer-item-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-3 h-3 text-slate-300" />
                    <Type className="w-3 h-3 text-slate-500" />
                    <span className="text-xs flex-1 truncate">{layer.text || "(vacio)"}</span>
                    <button onClick={(e) => { e.stopPropagation(); updateLayer(idx, { visible: !layer.visible }); }} className="text-slate-400 hover:text-slate-600">
                      {layer.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); moveLayer(idx, -1); }} className="text-slate-400 hover:text-slate-600">
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); moveLayer(idx, 1); }} className="text-slate-400 hover:text-slate-600">
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); removeLayer(idx); }} className="text-red-400 hover:text-red-600" data-testid={`remove-layer-${idx}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
              {layers.length === 0 && (
                <p className="text-xs text-slate-400 p-3 text-center">Agregue textos con el boton "+"</p>
              )}
            </div>

            {/* Selected layer properties */}
            {selectedLayer !== null && layers[selectedLayer] && (
              <div className="p-3 border-t space-y-3 bg-slate-50" data-testid="layer-properties">
                <p className="font-semibold text-xs text-slate-600 uppercase tracking-wider">Propiedades</p>

                <div>
                  <Label className="text-xs">Texto / Variable</Label>
                  <Input
                    value={layers[selectedLayer].text}
                    onChange={(e) => updateLayer(selectedLayer, { text: e.target.value })}
                    className="text-sm h-8 mt-1"
                    placeholder="{{nombre_usuario}}"
                    data-testid="layer-text-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Posicion X (%)</Label>
                    <Input
                      type="number" min="0" max="100" step="1"
                      value={layers[selectedLayer].x}
                      onChange={(e) => updateLayer(selectedLayer, { x: parseFloat(e.target.value) || 0 })}
                      className="text-sm h-8 mt-1"
                      data-testid="layer-x-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Posicion Y (%)</Label>
                    <Input
                      type="number" min="0" max="100" step="1"
                      value={layers[selectedLayer].y}
                      onChange={(e) => updateLayer(selectedLayer, { y: parseFloat(e.target.value) || 0 })}
                      className="text-sm h-8 mt-1"
                      data-testid="layer-y-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Tamano (px)</Label>
                    <Input
                      type="number" min="8" max="120" step="1"
                      value={layers[selectedLayer].fontSize}
                      onChange={(e) => updateLayer(selectedLayer, { fontSize: parseInt(e.target.value) || 16 })}
                      className="text-sm h-8 mt-1"
                      data-testid="layer-fontsize-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Color</Label>
                    <div className="flex gap-1 mt-1">
                      <input
                        type="color"
                        value={layers[selectedLayer].color}
                        onChange={(e) => updateLayer(selectedLayer, { color: e.target.value })}
                        className="w-8 h-8 rounded border cursor-pointer"
                        data-testid="layer-color-picker"
                      />
                      <Input
                        value={layers[selectedLayer].color}
                        onChange={(e) => updateLayer(selectedLayer, { color: e.target.value })}
                        className="text-sm h-8 flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Fuente</Label>
                  <Select value={layers[selectedLayer].fontFamily} onValueChange={(v) => updateLayer(selectedLayer, { fontFamily: v })}>
                    <SelectTrigger className="h-8 text-sm mt-1" data-testid="layer-font-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONTS.map(f => (
                        <SelectItem key={f.id} value={f.id} style={{ fontFamily: f.id }}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-1">
                  <Button
                    size="sm" variant={layers[selectedLayer].fontWeight === "bold" ? "default" : "outline"}
                    className="h-8 text-xs"
                    onClick={() => updateLayer(selectedLayer, { fontWeight: layers[selectedLayer].fontWeight === "bold" ? "normal" : "bold" })}
                    data-testid="layer-bold-btn"
                  >
                    <strong>B</strong>
                  </Button>
                  <Button
                    size="sm" variant={layers[selectedLayer].fontStyle === "italic" ? "default" : "outline"}
                    className="h-8 text-xs"
                    onClick={() => updateLayer(selectedLayer, { fontStyle: layers[selectedLayer].fontStyle === "italic" ? "normal" : "italic" })}
                    data-testid="layer-italic-btn"
                  >
                    <em>I</em>
                  </Button>
                  <Select value={layers[selectedLayer].textAlign} onValueChange={(v) => updateLayer(selectedLayer, { textAlign: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="layer-align-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Izq</SelectItem>
                      <SelectItem value="center">Centro</SelectItem>
                      <SelectItem value="right">Der</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Sombra de texto</Label>
                  <Select
                    value={layers[selectedLayer].textShadow}
                    onValueChange={(v) => updateLayer(selectedLayer, { textShadow: v })}
                  >
                    <SelectTrigger className="h-8 text-sm mt-1" data-testid="layer-shadow-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin sombra</SelectItem>
                      <SelectItem value="1px 1px 2px rgba(0,0,0,0.3)">Sutil</SelectItem>
                      <SelectItem value="2px 2px 4px rgba(0,0,0,0.5)">Media</SelectItem>
                      <SelectItem value="3px 3px 6px rgba(0,0,0,0.7)">Fuerte</SelectItem>
                      <SelectItem value="0 0 10px rgba(255,255,255,0.8)">Brillo blanco</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
